import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from 'Components/UI/Typography';
import Button from 'Components/UI/Button';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import Uploader from 'Components/Files/MessageAttachments/Uploader';
import MetaControl from 'Components/UI/Meta/MetaControl';
import closeModal from 'Helpers/ModalHelper';
import { withStyles } from '@material-ui/core/styles';
import LoadingOverlay from 'Components/UI/LoadingOverlay/LoadingOverlay';

const styles = theme => ({
  root: {},
  dialogPaper: {
    width: '100%',
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.facet.darkGray,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
});

const AttachInvestigationDocumentModal = ({ classes, investigationURL, onClose }) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [investigationResource, setInvestigationResource] = React.useState(null);
  const [documentResource, setDocumentResource] = React.useState(null);
  const [documentDirty, setDocumentDirty] = React.useState(false);

  const refreshInvestigationResource = async () => {
    setIsLoading(true);

    try {
      const collection = fr.collection(await fr.http.Get(investigationURL));
      setInvestigationResource(collection.resources()?.head());
    } catch (err) {
      displayErrorMessage(err, err.errors);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    refreshInvestigationResource();
  }, []);
  React.useEffect(() => {
    if (investigationResource?.attr('documents').resources()?.head()) {
      setDocumentResource(investigationResource?.attr('documents').resources().head());
    }
    setDocumentResource(investigationResource?.attr('documents').create());
  }, [investigationResource]);

  return (
    <Dialog
      classes={{
        root: classes.root,
        paper: classes.dialogPaper,
      }}
      maxWidth="sm"
      open
      onClose={closeModal}
    >
      {(isLoading || !investigationResource) && <LoadingOverlay />}
      <DialogTitle>
        <Typography
          className={classes.title}
        >
          Attach Document To Investigation
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        {documentResource && (
          <>
            <MetaControl
              attrName="documentType"
              resource={documentResource}
              onChange={(value) => {
                documentResource.attr('documentType', value);
                setDocumentDirty(!documentDirty);
              }}
            />
            <MetaControl
              attrName="tag"
              resource={documentResource}
              onChange={(value) => {
                documentResource.attr('tag', value);
                setDocumentDirty(!documentDirty);
              }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={(e) => {
            e.preventDefault();
            onClose();
          }}
          color="primary"
        >
          {'Cancel'}
        </Button>
        <Uploader
          url={investigationResource?.relationship('attachDocument').link('related').url()}
          onStart={() => setIsLoading(true)}
          onSuccess={() => {
            setIsLoading(false);
            onClose();
          }}
          onCatch={(err) => {
            displayErrorMessage(err, err.errors);
            setIsLoading(false);
            onClose();
          }}
          successToastMessage="Successfully attached document"
          additionalFormData={{
            investigationData: {
              attributes: {
                tag: documentResource?.attr('tag'),
                documentType: documentResource?.attr('documentType'),
              },
            },
          }}
          disableClick={!documentResource || !documentResource.attr('documentType') || !documentResource.attr('tag')}
        >
          <Button
            startIcon={<AttachFileIcon />}
          >
            Upload Document
          </Button>
        </Uploader>
      </DialogActions>
    </Dialog>
  );
};

AttachInvestigationDocumentModal.propTypes = {
  classes: PropTypes.object.isRequired,
  investigationURL: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default withStyles(styles)(AttachInvestigationDocumentModal);
